import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useSpring, useTransform } from 'framer-motion';
import axios from 'axios';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


export function LiveDirectoryMap({ onStateClick }) {
  const [components, setComponents] = useState(null);
  const [geographies, setGeographies] = useState(null);
  const [serverPoints, setServerPoints] = useState([]);
  const [hovered, setHovered] = useState(null);
  const containerRef = useRef(null);

  // Scroll-driven 3D camera tilt — subtle Apple/Vercel-style perspective change
  // as the map scrolls through the viewport.
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });
  const rxRaw    = useTransform(scrollYProgress, [0, 0.5, 1], [14, 0, -8]);
  const scaleRaw = useTransform(scrollYProgress, [0, 0.5, 1], [0.94, 1, 0.96]);
  const yRaw     = useTransform(scrollYProgress, [0, 0.5, 1], [40, 0, -30]);
  const glowRaw  = useTransform(scrollYProgress, [0, 0.5, 1], [0.35, 0.7, 0.35]);
  const springCfg = { stiffness: 80, damping: 22, mass: 0.6 };
  const rx    = useSpring(rxRaw,    springCfg);
  const scale = useSpring(scaleRaw, springCfg);
  const y     = useSpring(yRaw,     springCfg);
  const glow  = useSpring(glowRaw,  springCfg);

  // Load map deps + topojson (parallel with real-data fetch)
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      import('react-simple-maps'),
      fetch(GEO_URL).then((r) => r.json()),
    ]).then(([mapsMod, geoData]) => {
      if (cancelled) return;
      setComponents({
        ComposableMap: mapsMod.ComposableMap,
        Geographies: mapsMod.Geographies,
        Geography: mapsMod.Geography,
        Marker: mapsMod.Marker,
      });
      setGeographies(geoData);
    });

    // Fetch real server coordinates from backend
    axios.get(`${API}/public/directory-map`).then((res) => {
      if (cancelled) return;
      const real = (res?.data?.servers || []).map((s) => [
        s.city || s.name || 'Server',
        s.state || '',
        s.lng,
        s.lat,
        s.score || 80,
        true,           // isReal — render in real-data styling (cobalt)
        !!s.verified,   // pulse only when verified
      ]);
      // Show only real, registered servers — no demo/placeholder pins.
      setServerPoints(real);
    }).catch(() => {
      setServerPoints([]);
    });

    return () => { cancelled = true; };
  }, []);

  const totalActive = serverPoints.length;

  if (!components || !geographies) {
    return (
      <div className="relative aspect-[5/3] w-full rounded-2xl bg-slate-100 dark:bg-slate-900/40 border border-slate-200 dark:border-white/10 flex items-center justify-center">
        <div className="animate-pulse text-sm text-slate-400">Loading directory map…</div>
      </div>
    );
  }

  const { ComposableMap, Geographies, Geography, Marker } = components;

  return (
    <div ref={containerRef} className="relative w-full" data-testid="live-directory-map" style={{ perspective: '1400px' }}>
      <div className="absolute top-4 left-4 z-10 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 dark:bg-white/10 backdrop-blur border border-slate-200 dark:border-white/10 shadow-sm">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-[hsl(150,80%,55%)] opacity-60 animate-ping" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-[hsl(150,80%,55%)]" />
        </span>
        <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
          <span className="font-mono tabular-nums text-[hsl(150,80%,40%)] dark:text-[hsl(150,80%,60%)]">{totalActive.toLocaleString()}</span>
          <span className="ml-1 text-slate-500 dark:text-slate-400">servers · live</span>
        </span>
      </div>

      {/* Soft cobalt aurora underneath the map that brightens at peak focus */}
      <motion.div
        aria-hidden
        className="absolute inset-x-8 -bottom-8 h-32 rounded-[50%] blur-3xl pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, hsl(226 100% 55% / 0.35) 0%, transparent 70%)',
          opacity: glow,
        }}
      />

      <motion.div
        style={{
          rotateX: rx,
          scale,
          y,
          transformPerspective: 1400,
          transformOrigin: '50% 60%',
          willChange: 'transform',
        }}
      >
        <ComposableMap
        projection="geoAlbersUsa"
        projectionConfig={{ scale: 1000 }}
        width={900}
        height={540}
        style={{ width: '100%', height: 'auto' }}
      >
        <Geographies geography={geographies}>
          {({ geographies: geos }) =>
            geos.map((geo) => (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                onClick={() => {
                  const name = geo.properties.name;
                  if (onStateClick) onStateClick({ name, code: '' });
                }}
                className="map-geo"
                style={{
                  default: {
                    fill: 'var(--map-fill)',
                    stroke: 'var(--map-stroke)',
                    strokeWidth: 0.6,
                    outline: 'none',
                  },
                  hover: {
                    fill: 'var(--map-hover)',
                    stroke: 'hsl(226, 100%, 65%)',
                    strokeWidth: 0.9,
                    outline: 'none',
                    cursor: 'pointer',
                  },
                  pressed: { outline: 'none' },
                }}
              />
            ))
          }
        </Geographies>

        {serverPoints.map(([city, state, lng, lat, score, isReal, isVerified], idx) => (
          <Marker
            key={`${city}-${state}-${idx}`}
            coordinates={[lng, lat]}
            onMouseEnter={() => setHovered({ city, state, score, isReal, isVerified })}
            onMouseLeave={() => setHovered(null)}
            onClick={() => onStateClick && onStateClick({ name: state, code: state })}
            style={{ default: { cursor: 'pointer' }, hover: { cursor: 'pointer' } }}
          >
            {isVerified && (
              <circle r="8" fill="hsl(150 80% 55%)" opacity="0.22">
                <animate attributeName="r" from="6" to="16" dur="2s" repeatCount="indefinite" />
                <animate attributeName="opacity" from="0.45" to="0" dur="2s" repeatCount="indefinite" />
              </circle>
            )}
            <circle
              r={isReal ? 3.2 : 2.4}
              fill={isVerified ? 'hsl(150 80% 55%)' : (isReal ? 'hsl(226 100% 65%)' : 'hsl(226 100% 65%)')}
              stroke="var(--map-bg)"
              strokeWidth="1"
            />
          </Marker>
        ))}
      </ComposableMap>
      </motion.div>

      {/* Hover tooltip */}
      {hovered && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute pointer-events-none top-4 right-4 px-3 py-2 rounded-xl bg-slate-900 text-white text-xs shadow-lg border border-white/10"
        >
          <div className="font-bold tracking-tight">
            {hovered.city}{hovered.state ? `, ${hovered.state}` : ''}
          </div>
          <div className="flex items-center gap-2 mt-0.5 text-slate-300">
            <span className="font-mono text-[hsl(226,100%,70%)]">Score · {hovered.score}</span>
            {hovered.isReal && <span className="font-mono text-[hsl(150,80%,60%)]">· LIVE</span>}
          </div>
        </motion.div>
      )}

      {/* CSS vars for light/dark map */}
      <style>{`
        .light-theme [data-testid="live-directory-map"] { --map-fill: hsl(220 16% 92%); --map-stroke: hsl(220 16% 80%); --map-hover: hsl(226 100% 96%); --map-bg: hsl(0 0% 100%); }
        .dark-theme [data-testid="live-directory-map"], .dark [data-testid="live-directory-map"] { --map-fill: hsl(220 25% 11%); --map-stroke: hsl(220 25% 18%); --map-hover: hsl(220 25% 16%); --map-bg: hsl(220 40% 5%); }
      `}</style>
    </div>
  );
}

export default LiveDirectoryMap;
